// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Scripted fleets for /fleet/demo/:scenario.
//
// Why this file exists, and the rule it follows
// ---------------------------------------------------------------------------
// The fleet map's framing (autoFit.ts) is a pure function of where the sites
// are, and its labelling (labels.ts) of how close together they land — so the
// only way to exercise either is to feed it fleets of different shapes. The
// real fleet is one site (plus its mock twin), which exercises neither.
//
// ONE dataset, five framings
// ---------------------------------------------------------------------------
// There is exactly one set of sites here: the five Southeast data centers the
// design mock draws. Every scenario is a SUBSET of it, so the same five
// markers move between scenarios rather than each scenario inventing a fleet
// of its own — which is both less to keep honest and closer to what actually
// varies in practice (a fleet grows, a region is filtered, a site is the only
// one you have).
//
// So these are FIXTURES, not data. They are reachable ONLY from
// /fleet/demo/:scenario, they never touch /fleet, and nothing here is
// exported into the real page's data path: the demo screen builds its own
// site list, alarm counts and postures from this file and skips the alarm
// fetch entirely. That containment is the point — see
// docs/design/mock-data-philosophy.md on why invented numbers must never be
// able to reach a screen that reads as live.
//
// Every coordinate below is a real city centre (the same public lat/lon pairs
// any atlas gives), so the markers land where a reader expects. The sites,
// their alarm counts and their postures are invented, which the demo banner
// says on every scenario.

import type { Severity } from '../../../types/alarm';
import type { SitePosture } from '../../../types/sitePosture';

export interface FleetDemoSite {
  siteId: string;
  siteKey: string;
  label: string;
  /** Human-readable place, shown under a clustered marker. */
  place: string;
  lat: number;
  lon: number;
  counts: Record<Severity, number>;
  /**
   * Scripted posture — the marker's colour and the card's status pill.
   *
   * Demo-only. The real fleet has no posture rule yet and reports every site
   * as healthy; see types/sitePosture.ts.
   */
  posture: SitePosture;
}

export interface FleetDemoScenario {
  /** URL segment: /fleet/demo/<id>. */
  id: string;
  title: string;
  /** What this fleet is for — rendered in the demo banner. */
  blurb: string;
  sites: readonly FleetDemoSite[];
}

/** Terse helper — a demo site's P1-P4 counts, with the Unknown bucket at 0. */
function counts(p1: number, p2: number, p3: number, p4: number): Record<Severity, number> {
  return { P1: p1, P2: p2, P3: p3, P4: p4, Unknown: 0 };
}

/**
 * The dataset: the five sites drawn on the design's fleet map, with their
 * real coordinates and the postures the mock colours them with.
 *
 * Posture lives on the site, not on the scenario, so a site looks the same
 * wherever it appears — Atlanta is at risk in every scenario that contains
 * it. A scenario that contradicted itself between framings would make the
 * demo useless for talking about what the colours mean.
 */
const DESIGN_FLEET = {
  nashville: {
    siteId: 'demo-bna-tn-01',
    siteKey: 'site_bna_tn_01',
    label: 'Nashville, TN',
    place: 'Nashville, TN',
    lat: 36.1627,
    lon: -86.7816,
    counts: counts(0, 1, 3, 5),
    posture: 'healthy',
  },
  chattanooga: {
    siteId: 'demo-cha-tn-02',
    siteKey: 'site_cha_tn_02',
    label: 'Chattanooga, TN',
    place: 'Chattanooga, TN',
    lat: 35.0456,
    lon: -85.3097,
    counts: counts(0, 0, 2, 4),
    posture: 'planned',
  },
  atlanta: {
    siteId: 'demo-atl-ga-01',
    siteKey: 'site_atl_ga_01',
    label: 'Atlanta, GA',
    place: 'Atlanta, GA',
    lat: 33.749,
    lon: -84.388,
    counts: counts(4, 3, 6, 9),
    posture: 'at-risk',
  },
  greenville: {
    siteId: 'demo-gsp-sc-01',
    siteKey: 'site_gsp_sc_01',
    label: 'Greenville, SC',
    place: 'Greenville, SC',
    lat: 34.8526,
    lon: -82.394,
    counts: counts(0, 1, 1, 2),
    posture: 'planned',
  },
  tampa: {
    siteId: 'demo-tpa-fl-01',
    siteKey: 'site_tpa_fl_01',
    label: 'Tampa, FL',
    place: 'Tampa, FL',
    lat: 27.9506,
    lon: -82.4572,
    counts: counts(2, 2, 4, 7),
    posture: 'at-risk',
  },
} satisfies Record<string, FleetDemoSite>;

/**
 * The scenarios, in URL order.
 *
 * Each is a fleet this dataset could plausibly BE — the whole set, one
 * region of it, its outer pair, a single site, its dense middle — and each
 * lands on a different framing: the fleet's own box, the minimum-span floor,
 * and the crowded case where labels have to be laid out around each other.
 */
export const FLEET_DEMO_SCENARIOS: readonly FleetDemoScenario[] = [
  {
    id: '1',
    title: 'Whole fleet — five Southeast sites',
    blurb:
      'Every data center on the design mock, with its scripted posture. Frames a multi-state region: the ordinary case.',
    sites: [
      DESIGN_FLEET.nashville,
      DESIGN_FLEET.chattanooga,
      DESIGN_FLEET.atlanta,
      DESIGN_FLEET.greenville,
      DESIGN_FLEET.tampa,
    ],
  },
  {
    id: '2',
    title: 'Tennessee only — Nashville and Chattanooga',
    blurb:
      'The two Tennessee sites, about 180 km apart. Their own bounding box is far too small to read, so this is the case where the minimum-span floor decides the zoom rather than the fleet does.',
    sites: [DESIGN_FLEET.nashville, DESIGN_FLEET.chattanooga],
  },
  {
    id: '3',
    title: 'The outer pair — Nashville and Tampa',
    blurb:
      'The fleet’s two extremes, roughly 1,100 km apart. Wider than the floor, so the frame follows the sites — the direct contrast with scenario 2, and the same box scenario 1 fits, since these are the sites that decide it.',
    sites: [DESIGN_FLEET.nashville, DESIGN_FLEET.tampa],
  },
  {
    id: '4',
    title: 'One site — Atlanta',
    blurb:
      'A single data center: the shape of the real fleet today. A one-point bounding box has no width at all, so the floor supplies the entire zoom, and the frame has to stay recognisable as Georgia rather than a wall of one state’s interior.',
    sites: [DESIGN_FLEET.atlanta],
  },
  {
    id: '5',
    title: 'The dense middle — Chattanooga, Atlanta and Greenville',
    blurb:
      'Three sites inside a 300 km triangle. The tightest arrangement in the dataset: the markers nearly touch, so the labels have to be placed around one another instead of each sitting above its own dot.',
    sites: [DESIGN_FLEET.chattanooga, DESIGN_FLEET.atlanta, DESIGN_FLEET.greenville],
  },
];

/** Look a scenario up by URL segment; `undefined` for anything else. */
export function findFleetDemoScenario(id: string | undefined): FleetDemoScenario | undefined {
  return FLEET_DEMO_SCENARIOS.find(s => s.id === id);
}

// There is deliberately no default scenario: /fleet/demo lists them (see
// AppRoutes) rather than landing on a scripted fleet nobody asked for.
